/**
 * Redux slice for org-scoped estimate value tiers (`estimate_options` in Firestore).
 *
 * Mirrors {@link boOptionsSlice}: loaded at auth bootstrap via
 * {@link fetchEstimateOptions} and consumed by exception-flow urology worklists
 * (Estimate Value filter, radio modal, mark-as-completed badge).
 *
 * @module redux/slice/estimateOptionsSlice
 */
import { createSlice } from "@reduxjs/toolkit";
import { FirestoreService } from "../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../api/firebase/references";
import {
  BOOptionModel,
  BOOptionModelResponse,
} from "../../data-model/boOptionModel";

/** Redux state for org-scoped estimate option tiers loaded from Firestore. */
export interface EstimateOptionsState {
  /** Estimate tiers from `healthcare_facility/{id}/options/estimate_options`. */
  data: BOOptionModel[];
  /** True while {@link fetchEstimateOptions} is in flight. */
  loading: boolean;
  /** Error message when the Firestore fetch fails; cleared on success. */
  error: string | null;
}

export const initialState: EstimateOptionsState = {
  data: [],
  loading: false,
  error: null,
};

const estimateOptionsSlice = createSlice({
  name: "estimateOptions",
  initialState,
  reducers: {
    setEstimateOptions: (state, action) => {
      state.data = action.payload;
      state.loading = false;
      state.error = null;
    },
    fetchingEstimateOptions: (state) => {
      state.loading = true;
      state.error = null;
      state.data = [];
    },
    setEstimateOptionsError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
      state.data = [];
    },
  },
});

export const {
  setEstimateOptions,
  fetchingEstimateOptions,
  setEstimateOptionsError,
} = estimateOptionsSlice.actions;

/**
 * Async thunk that loads estimate option tiers for a healthcare facility.
 *
 * No-ops when a fetch is already in progress. On success, stores
 * `response.data` (array of {@link BOOptionModel}) in the slice.
 *
 * @param orgId - Healthcare facility id used to resolve the Firestore document path
 * @returns Redux thunk; resolves when the slice is updated or an error is recorded
 */
export const fetchEstimateOptions =
  (orgId: string) => async (dispatch, getState) => {
    const { estimateOptions } = getState();
    if (estimateOptions.loading) {
      return;
    }

    dispatch(fetchingEstimateOptions());
    try {
      const response =
        await FirestoreService.getDocument<BOOptionModelResponse>(
          FirestoreDocumentReference.estimateOptions(orgId),
        );

      dispatch(setEstimateOptions(response.data));
    } catch (error) {
      dispatch(setEstimateOptionsError(error as string));
    }
  };

export default estimateOptionsSlice.reducer;
